import { selectNativeCall } from './selectors'
import { v4 as uuid } from 'uuid'
import { Platform } from 'react-native'
import DeviceInfo from 'react-native-device-info'
import RNCallKeep from 'react-native-callkeep'
import PushNotification from 'react-native-push-notification'
import { RootState } from '../CreateStore'
import { put, select, delay, call } from 'typed-redux-saga'
import { nativeCallActions } from './slice'
import I18n from 'APP/Services/i18n'
import { videoSessionActions } from '../VideoSession'

const TOTAL_LOCAL_NOTIFICATION_COUNT = 6
export const NATIVE_CALL_TIMEOUT = 60000

export const patientState = (state: RootState) => state.patient
export const featuresState = (state: RootState) => (state && state.features) || {}
export const videoSessionState = (state: RootState) => state.videoSession

// Only physical iOS device can use native call
export function* canUseNativeCall() {
  const features = yield* select(featuresState)
  return features?.useNativeCall && !DeviceInfo.isEmulatorSync() && Platform.OS === 'ios'
}

export function* incomingCall() {
  const nativeCall = yield* select(selectNativeCall)
  const features = yield* select(featuresState)

  if (Platform.OS === 'ios' && !DeviceInfo.isEmulatorSync()) {
    const callerName = nativeCall.callerName || I18n.t('VideoCallLocalNotification.defaultCallerName')

    if (features?.useNativeCall) {
      // new experience - use iOS CallKit
      yield* put(nativeCallActions.displayNativeCall({
        callerName: callerName,
        uuid: uuid(),
      }))
    } else {
      // legacy experience - schedule local notifications
      yield* put(nativeCallActions.displayLocalNotifications(
        callerName,
      ))
    }
  }
}

export function* checkUnresponsiveCall() {
  const { accepted, declined } = yield* select(selectNativeCall)

  if (!accepted && !declined) {
    yield* put(nativeCallActions.endUnresponsiveCall())
  }
}

export function* displayNativeCall() {
  const { callerName, uuid } = yield* select(selectNativeCall)

  if (Platform.OS === 'ios' && !DeviceInfo.isEmulatorSync()) {
    RNCallKeep.displayIncomingCall(
      uuid,
      callerName,
      callerName,
      'generic',
      true,
    )
    yield delay(NATIVE_CALL_TIMEOUT)
    yield* call(checkUnresponsiveCall)
  }
}

export function* displayLocalNotifications() {
  const { callerName } = yield* select(selectNativeCall)

  if (Platform.OS === 'ios' && !DeviceInfo.isEmulatorSync()) {
    for (let i = 1; i < TOTAL_LOCAL_NOTIFICATION_COUNT; i++) {
      PushNotification.localNotificationSchedule({
        title: I18n.t('VideoCallLocalNotification.title') ?? 'Video call',
        message: I18n.t('VideoCallLocalNotification.message', { name: callerName }),
        soundName: 'phone_ringing.wav',
        category: "VIDEO_CALL",
        date: new Date(Date.now() + 2000 * i),
      })
    }
  }
}

export function* nativeCallAccepted() {
  // Skip incoming call screen, directly attempts to connect to the call
  const isNativeCall = yield* call(canUseNativeCall)

  if (isNativeCall) {
    const lastCallAcceptedAt = Date.now()
    const patient = yield* select(patientState)
    yield* put(
      videoSessionActions.patientAcceptedCall({
        userId: patient.profile.id,
        lastCallAcceptedAt,
      })
    )
  }
}

export function* nativeCallDeclined() {
  const patient = yield* select(patientState)
  const { callId } = yield* select(videoSessionState)

  if (callId) {
    yield* put(videoSessionActions.patientEndedCall({ userId: patient.profile.id, declined: true }))
  }
}

export function* endNativeCall() {
  if (Platform.OS === 'ios' && !DeviceInfo.isEmulatorSync()) {
    RNCallKeep.endAllCalls()
  }
}

export function* endUnresponsiveCall() {
  yield* call(endNativeCall)
  yield* call(nativeCallDeclined)
}
